import React, { Component } from "react";
import { connect } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import Select from "react-select";
import { BASE_URL } from "../../../../_constants/urlConstants";
import equipmentActions from "../../../../_actions/equipmentActions";

const CATALOG_RATE_URL = `${BASE_URL}/api/ProjectEquipment/Catalog/Rate`;
const CATALOG_MODELS_URL = `${BASE_URL}/api/ProjectEquipment/Catalog/Models`;

const norm = (s) => (s || "").trim().toUpperCase();

function buildYearOptions() {
  const d = new Date();
  const fyStartYear = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return Array.from({ length: 6 }, (_, i) => {
    const y = fyStartYear - i;
    return {
      value: `${y}-04-01`,
      label: `FY ${String(y).slice(2)}/${String(y + 1).slice(2)}`,
    };
  });
}

const YEAR_OPTIONS = buildYearOptions();

const YEAR_SELECT_STYLES = {
  control: (p) => ({ ...p, minHeight: 26, height: 26 }),
  valueContainer: (p) => ({ ...p, padding: "0 6px" }),
  dropdownIndicator: (p) => ({ ...p, padding: 2 }),
  indicatorSeparator: () => ({ display: "none" }),
  menu: (p) => ({ ...p, zIndex: 9999, fontSize: "0.8rem" }),
};

class EquipmentDetails extends Component {
  state = {
    selectedYear: YEAR_OPTIONS[0],
    rate: null,
    loadingRate: false,
    resolvedModelDescription: null,
  };

  componentDidMount() {
    this.fetchRate();
    this.fetchModelDescription();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.equipmentId !== this.props.equipmentId) {
      this.setState(
        { rate: null, selectedYear: YEAR_OPTIONS[0], resolvedModelDescription: null },
        () => {
          this.fetchRate();
          this.fetchModelDescription();
        }
      );
    }
  }

  // Legacy equipment saved before ModelDescription was persisted may have a blank
  // value on the stored record (the Edit modal self-corrects this the same way on
  // open). Best-effort: look up the live catalog and fill in the description for
  // display only — never written back to the record here.
  fetchModelDescription = async () => {
    const { equipment } = this.props;
    const { selectedYear } = this.state;
    if (
      !equipment ||
      equipment.isManualEntry ||
      equipment.equipmentModelDescription ||
      !equipment.equipmentModel ||
      !equipment.equipmentClass ||
      !equipment.equipmentMake
    ) {
      this.setState({ resolvedModelDescription: null });
      return;
    }
    try {
      const res = await axios.get(CATALOG_MODELS_URL, {
        params: {
          equipmentClass: equipment.equipmentClass,
          equipmentMake: equipment.equipmentMake,
          equipmentType: equipment.equipmentType,
          reportDate: selectedYear.value,
        },
      });
      const match = (res.data || []).find(
        (m) => norm(m.equipmentModel) === norm(equipment.equipmentModel)
      );
      this.setState({ resolvedModelDescription: match ? match.equipmentModelDescription : null });
    } catch {
      this.setState({ resolvedModelDescription: null });
    }
  };

  fetchRate = async () => {
    const { equipment } = this.props;
    const { selectedYear } = this.state;
    if (!equipment || equipment.isManualEntry || !equipment.equipmentClass || !equipment.equipmentMake) {
      this.setState({ rate: null });
      return;
    }
    this.setState({ loadingRate: true });
    try {
      const res = await axios.get(CATALOG_RATE_URL, {
        params: {
          equipmentClass: equipment.equipmentClass,
          equipmentMake: equipment.equipmentMake,
          equipmentModel: equipment.equipmentModel || null,
          equipmentType: equipment.equipmentType,
          reportDate: selectedYear.value,
        },
      });
      this.setState({ rate: res.data || null, loadingRate: false });
    } catch {
      this.setState({ rate: null, loadingRate: false });
    }
  };

  handleYearChange = (selectedYear) => {
    this.setState({ selectedYear, rate: null, resolvedModelDescription: null }, () => {
      this.fetchRate();
      this.fetchModelDescription();
    });
  };

  handleClick_deleteEquipment = (e) => {
    e.preventDefault();
    this.props.toggleAlert_deleteEquipment();
  };

  handleClick_editEquipment = (e) => {
    e.preventDefault();
    this.props.editEquipment();
  };

  handleClick_mergeEquipment = (e) => {
    e.preventDefault();
    this.props.mergeEquipment();
  };

  handleClick_linkToProject = (e) => {
    e.preventDefault();
    const { dispatch, projectId, equipment } = this.props;
    dispatch(equipmentActions.linkToProject(projectId, equipment.contractorId, equipment.equipmentId));
  };

  render() {
    const { equipment, viewMode } = this.props;
    const { selectedYear, rate, loadingRate, resolvedModelDescription } = this.state;
    const modelDescription = equipment && (equipment.equipmentModelDescription || resolvedModelDescription);
    const isAllAvailableView = viewMode === "allAvailable";
    const alreadyLinked = !!(equipment && equipment.isLinked);
    const showRate = equipment &&
      !equipment.isManualEntry &&
      !!equipment.equipmentClass &&
      !!equipment.equipmentMake;
    // Manual Entry equipment carries its own static rate (no catalog lookup, no fiscal
    // year to pick — it's just whatever was typed into the Edit modal).
    const showManualRate = equipment && equipment.isManualEntry &&
      (equipment.rate != null || equipment.delayFactor != null || equipment.otFactor != null);

    return (
      <React.Fragment>
        {equipment ? (
          <React.Fragment>
            <h3 className="card-title mx-0">{equipment.makeDesc || equipment.description}</h3>
            <h5 className="card-subtitle text-muted mx-0">
              {equipment.isManualEntry && (
                <span className="badge badge-secondary mr-2">Manual Entry</span>
              )}
            </h5>
          </React.Fragment>
        ) : (
          <h5 className="text-muted">No equipment selected</h5>
        )}
        {equipment && (
          <React.Fragment>
            <div className="row ml-0 mt-2">
              <div className="col pl-0">
                <ul className="list-group-flush border-bottom border-top mb-2 pl-0">
                  <li className="list-group-item py-1 pl-1">
                    <div className="row">
                      <div className="col pr-1"><b>Number:</b></div>
                      <div className="col pl-1">{equipment.number || "(N/A)"}</div>
                    </div>
                  </li>
                  {!equipment.isManualEntry && (
                    <li className="list-group-item py-1 pl-1">
                      <div className="row">
                        <div className="col pr-1"><b>Type:</b></div>
                        <div className="col pl-1">
                          {equipment.equipmentType === "E" ? "Standard" :
                           equipment.equipmentType === "M" ? "Miscellaneous" :
                           equipment.equipmentType || "(N/A)"}
                        </div>
                      </div>
                    </li>
                  )}
                  {(equipment.equipmentClass || equipment.equipmentMake || equipment.equipmentModel) && (
                    <React.Fragment>
                      <li className="list-group-item py-1 pl-1">
                        <div className="row">
                          <div className="col pr-1"><b>Class:</b></div>
                          <div className="col pl-1">
                            {equipment.equipmentClass
                              ? `[ ${equipment.equipmentClass} ]${equipment.equipmentClassDescription ? ` ${equipment.equipmentClassDescription}` : ""}`
                              : "(N/A)"}
                          </div>
                        </div>
                      </li>
                      <li className="list-group-item py-1 pl-1">
                        <div className="row">
                          <div className="col pr-1"><b>Make:</b></div>
                          <div className="col pl-1">
                            {equipment.equipmentMake
                              ? `[ ${equipment.equipmentMake} ]${equipment.equipmentMakeDescription ? ` ${equipment.equipmentMakeDescription}` : ""}`
                              : "(N/A)"}
                          </div>
                        </div>
                      </li>
                      <li className="list-group-item py-1 pl-1">
                        <div className="row">
                          <div className="col pr-1"><b>Model Code:</b></div>
                          <div className="col pl-1">
                            {equipment.equipmentModel
                              ? `[ ${equipment.equipmentModel} ]${modelDescription ? ` ${modelDescription}` : ""}`
                              : "(N/A)"}
                          </div>
                        </div>
                      </li>
                    </React.Fragment>
                  )}
                  <li className="list-group-item py-1 pl-1">
                    <div className="row">
                      <div className="col pr-1"><b>Description:</b></div>
                      <div className="col pl-1">{equipment.description || "(N/A)"}</div>
                    </div>
                  </li>
                  <li className="list-group-item py-1 pl-1">
                    <div className="row">
                      <div className="col pr-1"><b>Rental:</b></div>
                      <div className="col pl-1">{equipment.isRental ? "Yes" : "No"}</div>
                    </div>
                  </li>
                </ul>

                {showRate && (
                  <div className="mb-2">
                    <div className="d-flex align-items-center mb-1">
                      <small className="mr-2"><b>Rental Rate</b></small>
                      <div style={{ width: 112, fontSize: "0.8rem" }}>
                        <Select
                          value={selectedYear}
                          options={YEAR_OPTIONS}
                          onChange={this.handleYearChange}
                          isSearchable={false}
                          styles={YEAR_SELECT_STYLES}
                        />
                      </div>
                    </div>
                    {loadingRate ? (
                      <small className="text-muted">
                        <FontAwesomeIcon icon="spinner" spin className="fa-fw mr-1" />
                        Loading...
                      </small>
                    ) : rate ? (
                      <ul className="list-group-flush border-bottom border-top pl-0 mb-0">
                        <li className="list-group-item py-1 pl-1">
                          <div className="row">
                            <div className="col pr-1"><b>Rate:</b></div>
                            <div className="col pl-1">${rate.rate}/hr</div>
                          </div>
                        </li>
                        <li className="list-group-item py-1 pl-1">
                          <div className="row">
                            <div className="col pr-1"><b>OT Factor:</b></div>
                            <div className="col pl-1">{rate.otFactor}</div>
                          </div>
                        </li>
                        <li className="list-group-item py-1 pl-1">
                          <div className="row">
                            <div className="col pr-1"><b>Delay Factor:</b></div>
                            <div className="col pl-1">{rate.delayFactor}</div>
                          </div>
                        </li>
                      </ul>
                    ) : (
                      <small className="text-muted">No rate available for selected year.</small>
                    )}
                  </div>
                )}

                {showManualRate && (
                  <div className="mb-2">
                    <small className="mr-2"><b>Rate</b></small>
                    <ul className="list-group-flush border-bottom border-top pl-0 mb-0">
                      <li className="list-group-item py-1 pl-1">
                        <div className="row">
                          <div className="col pr-1"><b>Rate:</b></div>
                          <div className="col pl-1">{equipment.rate != null ? `$${equipment.rate}/hr` : "(N/A)"}</div>
                        </div>
                      </li>
                      <li className="list-group-item py-1 pl-1">
                        <div className="row">
                          <div className="col pr-1"><b>OT Factor:</b></div>
                          <div className="col pl-1">{equipment.otFactor != null ? equipment.otFactor : "(N/A)"}</div>
                        </div>
                      </li>
                      <li className="list-group-item py-1 pl-1">
                        <div className="row">
                          <div className="col pr-1"><b>Delay Factor:</b></div>
                          <div className="col pl-1">{equipment.delayFactor != null ? equipment.delayFactor : "(N/A)"}</div>
                        </div>
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
            <div className="form-row form-group align-middle">
              {isAllAvailableView ? (
                <button
                  type="button"
                  name="linkToProject"
                  id="linkToProject"
                  className="btn btn-info mr-2"
                  onClick={this.handleClick_linkToProject}
                  disabled={alreadyLinked}
                >
                  <FontAwesomeIcon icon="plus" className="fa-fw mr-2" />
                  {alreadyLinked ? "Already in Project" : "Add to Project"}
                </button>
              ) : (
                <React.Fragment>
                  <button
                    type="button"
                    name="editEquipmentDetails"
                    id="editEquipmentDetails"
                    className="btn btn-secondary mr-2"
                    onClick={this.handleClick_editEquipment}
                  >
                    Edit...
                  </button>
                  <button
                    type="button"
                    name="mergeEquipment"
                    id="mergeEquipment"
                    className="btn btn-warning mr-2"
                    onClick={this.handleClick_mergeEquipment}
                  >
                    Merge...
                  </button>
                  <button
                    type="button"
                    name="deleteEquipment"
                    id="deleteEquipment"
                    className="btn btn-danger mr-2"
                    onClick={this.handleClick_deleteEquipment}
                  >
                    Delete
                  </button>
                </React.Fragment>
              )}
            </div>
          </React.Fragment>
        )}
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const sourceList = ownProps.viewMode === "allAvailable"
    ? state.equipment.allAvailableEquipLists[ownProps.projectId]
    : state.equipment.projEquipLists[ownProps.projectId];
  return {
    equipment: ownProps.equipmentId
      ? (sourceList || []).find((e) => e.equipmentId === ownProps.equipmentId) ?? null
      : null,
  };
};

export default connect(mapStateToProps)(EquipmentDetails);
